﻿import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Download } from "lucide-react";

import useListController from "@/hooks/list/useListController";
import { useHistoriesLookups, useHistories } from "@/hooks/histories/useHistories";
import { useScreenPermissions } from "@/hooks/general/useScreenPermissions";
import { useHistoryExport } from "@/hooks/histories/useHistoryExport";
import { useExportOutputPDF } from "@/hooks/histories/useExportOutputPDF";
import useFilterStore from "@/stores/useFilterStore";
import { SCREEN_ID_OUTPUT_HISTORY } from "@/constants/screenIds";

import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomConfirmModal from "@/components/desktop/modal/CustomConfirmModal";
import CustomProcessingModal from "@/components/desktop/modal/CustomProcessingModal";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";

import { useOutputHistoryFilters } from "./hooks/useOutputHistoryFilters";
import { useOutputHistoryActions } from "./hooks/useOutputHistoryActions";
import { useOutputHistoryColumns } from "./hooks/useOutputHistoryColumns";

import IOForm from "@/pages/Histories/IO/IOForm/IOForm";
import IODistribution from "@/pages/Histories/IO/IODistribution/IODistribution";
import OutputHistoryDetails from "./OutputHistoryDetails";

import { OUTPUT_FILTERS, OUTPUT_METADATA, OUTPUT_BASE_TABLE } from "./output-history.constants";

import { getHistoryQuickActions } from "../Home/histories-metadata";

const OutputHistory = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { filter } = useFilterStore();
	const { estate_parent_id, estate_id, search, parameter } = filter;
	const { canShow, canEdit, canDelete, isLoading: isLoadingPermissions } = useScreenPermissions(SCREEN_ID_OUTPUT_HISTORY);
	const canLoadScreenData = !isLoadingPermissions && canShow;

	const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const action = searchParams.get("action");
	const historyIdParam = searchParams.get("id");

	const isModalOpen = useMemo(() => {
		return canEdit && action === "editar" && historyIdParam !== null && historyIdParam !== "";
	}, [action, canEdit, historyIdParam]);

	const outputCodeParam = searchParams.get("output_code");
	const balanceIdParam = searchParams.get("balance_id");

	const isAssignOAModalOpen = useMemo(() => {
		return canEdit && action === "asignar-oa" && Boolean(outputCodeParam) && Boolean(balanceIdParam);
	}, [action, balanceIdParam, canEdit, outputCodeParam]);

	const applicationHistoryIdParam = searchParams.get("application_history_id");

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [selectedHistoryId, setSelectedHistoryId] = useState(null);

	const {
		isFilterOpen,
		filtersState,
		appliedFiltersState,
		tableState,
		appliedFilterParams,
		reactTableSort,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		setFilterOptions,
	} = useListController({
		baseFilter: OUTPUT_FILTERS,
		baseTable: OUTPUT_BASE_TABLE,
	});

	const { data: lookups, isLoading: isLoadingLookups } = useHistoriesLookups(
		{
			type: "output",
			estate_id,
			estate_parent_id,
			search,
			parameter,
		},
		{
			enabled: canLoadScreenData,
		},
	);

	const { isReady } = useOutputHistoryFilters({
		lookups,
		filtersState,
		setFilterOptions,
		handleFilterChange,
		handleApplyFilters,
	});

	const filters = useMemo(
		() => ({
			...appliedFilterParams,
			estate_id,
			estate_parent_id,
			search,
			parameter,
			...tableState,
		}),
		[appliedFilterParams, estate_id, estate_parent_id, search, parameter, tableState],
	);

	const { data, isLoading, isFetching } = useHistories(OUTPUT_METADATA.apiType, filters, {
		enabled: canLoadScreenData && !isLoadingLookups && Boolean(lookups) && isReady,
	});

	const { handleEdit, handleAssignOA, handleDelete, isDeleting } = useOutputHistoryActions(OUTPUT_METADATA.apiType, filters);
	const { mutate: exportPDF, isLoading: isExportingPDF } = useExportOutputPDF();

	const handleExportPDFByRow = (outputRow) => {
		const applicationHistoryIDs = String(outputRow?.applications_ids || "")
			.split(",")
			.map((value) => Number(value.trim()))
			.filter((value) => Number.isInteger(value) && value > 0);

		if (applicationHistoryIDs.length > 0) {
			exportPDF(applicationHistoryIDs);
		}
	};

	const columns = useOutputHistoryColumns(tableState, {
		onAssignOA: canEdit ? handleAssignOA : null,
		onExportPDF: canShow ? handleExportPDFByRow : null,
	});

	const { handleExport, isAlreadyExporting } = useHistoryExport("output", appliedFilterParams);

	const exportButtons = useMemo(
		() => [
			{
				key: "export_excel",
				label: "Exportar",
				icon: Download,
				variant: "secondary",
				disabled: isAlreadyExporting,
				options: [
					{ key: "excel", label: "Excel General", onClick: () => handleExport("nuevo_excel") },
					{ key: "excel_detail", label: "Excel Detalle", onClick: () => handleExport("nuevo_excel_detalle") },
				],
			},
		],
		[handleExport, isAlreadyExporting],
	);

	const quickActions = useMemo(() => {
		const actions = getHistoryQuickActions("salida");
		return actions.map((action) => ({
			...action,
			onClick: () => navigate(action.path),
		}));
	}, [navigate]);

	const isDistributionOpen = useMemo(() => {
		return canEdit && action === "distribucion" && Boolean(applicationHistoryIdParam);
	}, [canEdit, action, applicationHistoryIdParam]);

	const handleDistribution = (applicationHistoryId) => {
		navigate(`/historiales/salida?action=distribucion&application_history_id=${applicationHistoryId}`);
	};

	const renderExpandedContent = (row) => {
		return (
			<OutputHistoryDetails
				items={row.original.details || []}
				onEdit={canEdit ? handleEdit : null}
				onDistribution={canEdit ? handleDistribution : null}
				onDelete={
					canDelete
						? (historyId) => {
								setSelectedHistoryId(historyId);
								setShowDeleteModal(true);
							}
						: null
				}
			/>
		);
	};

	const confirmDelete = async () => {
		if (!selectedHistoryId) return;
		await handleDelete(selectedHistoryId);
		setShowDeleteModal(false);
		setSelectedHistoryId(null);
	};

	if (isLoadingPermissions) {
		return <CustomFetching center message="Validando permisos..." />;
	}

	if (!canShow) {
		return <CustomNoPermissionView />;
	}

	return (
		<>
			<ListScaffold
				quickActions={quickActions}
				actionButtons={exportButtons}
				subtitle={OUTPUT_METADATA.subtitle}
				filterTitle={`Filtrar ${OUTPUT_METADATA.title}`}
				isFilterOpen={isFilterOpen}
				filters={filtersState}
				appliedFilters={appliedFiltersState}
				onFilterChange={handleFilterChange}
				onApplyFilters={handleApplyFilters}
				onClearFilters={handleClearAllFilters}
				onClearOneFilter={handleClearOneFilter}
				onOpenFilter={openFilter}
				onCloseFilter={closeFilter}
				isFetching={isFetching}
				isLoading={isLoading || isLoadingLookups}
				data={data?.data || []}
				totalItems={data?.totalRecords || 0}
				totalPages={data?.totalPages || 0}
				toItem={data?.data?.length || 0}
				fromItem={data?.page === 1 ? 1 : (data?.page - 1) * tableState.per_page + 1}
				pageIndex={tableState.page}
				columns={columns}
				onPageChange={handlePageChange}
				pageSize={tableState.per_page}
				onPageSizeChange={handlePageSizeChange}
				sortBy={reactTableSort}
				onSortByChange={handleSortByChange}
				pageSizeOptions={[20, 50, 100, 200]}
				initialPageSize={tableState.per_page}
				initialSortBy={reactTableSort}
				compactHeaders={true}
				compactBody={true}
				useExpandableRows={true}
				renderExpandedContent={renderExpandedContent}
			/>{" "}
			<CustomConfirmModal
				open={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={confirmDelete}
				title="Eliminar aplicación"
				message={
					<div>
						¿Está seguro que desea eliminar esta aplicación?
						<br />
						<br />
						Si elimina la aplicación seleccionada, no podrá recuperarla.
						<br />
						1. Si es una aplicación de salida, se eliminará su entrada asociada.
						<br />
						2. Si es una aplicación de traspaso, se eliminará el stock cargado en el campo de destino.
					</div>
				}
				confirmText="Sí, eliminar"
				cancelText="No, cancelar"
				isLoading={isDeleting}
			/>
			<CustomProcessingModal open={isExportingPDF} message="Generando PDF de salida..." />
			<CustomProcessingModal open={isDeleting} message="Eliminando aplicación de IO..." />
			<IOForm
				isOpen={isModalOpen}
				historyId={historyIdParam}
				listType={OUTPUT_METADATA.apiType}
				filters={{
					...appliedFilterParams,
					estate_id,
					estate_parent_id,
					search,
					parameter,
					...tableState,
				}}
				onClose={() => navigate("/historiales/salida")}
			/>
			<IOForm
				isOpen={isAssignOAModalOpen}
				outputCode={outputCodeParam}
				balanceId={balanceIdParam}
				listType={OUTPUT_METADATA.apiType}
				filters={{
					...appliedFilterParams,
					estate_id,
					estate_parent_id,
					search,
					parameter,
					...tableState,
				}}
				onClose={() => navigate("/historiales/salida")}
			/>
			<IODistribution
				isOpen={isDistributionOpen}
				applicationHistoryId={applicationHistoryIdParam}
				listType={OUTPUT_METADATA.apiType}
				filters={filters}
				onClose={() => navigate("/historiales/salida")}
			/>
		</>
	);
};

export default OutputHistory;
